import { useSearchGifsMutation } from "@api/media";
import Icon from "@components/ui/icon";
import { MediaLibFileCheckbox } from "@editor/modules/media-lib/components/files/file";
import { makeImagesPanelImageElement } from "@editor/modules/media-lib/utils";
import { cn } from "@lib/cn";
import { type ReactNode, memo, useMemo } from "react";
import type { ListRowProps } from "react-virtualized";
import { useIntersectionObserver } from "usehooks-ts";
import { GIF_MASONRY_GAPS, GIF_QUERY_KEY } from "../../constants";
import { useGifsCtx } from "../../ctx";
import type { IGifs } from "../../types";
import { GifSkeleton } from "../gif-skeleton";

interface IProps {
  rowData: ListRowProps;
  gifData?: IGifs | null;
}

export const GifsMasonryRow = ({ rowData: { style }, gifData }: IProps) => {
  const { ref, isIntersecting } = useIntersectionObserver({
    threshold: 0.1,
  });
  const [, { isLoading }] = useSearchGifsMutation({
    fixedCacheKey: GIF_QUERY_KEY,
  });
  const { isMultiSelect, onSelect, selectedItems, view } = useGifsCtx();

  const isSelected = useMemo(
    () => !!selectedItems?.find((el) => el.id === gifData?.id),
    [selectedItems, gifData?.id],
  );

  const height = (style.height as number) - GIF_MASONRY_GAPS;
  if (!gifData) {
    return (
      <div
        style={{
          ...style,
          height,
          opacity: isLoading ? 1 : 0,
        }}
      >
        <GifSkeleton />
      </div>
    );
  }

  return (
    <Wrapper gifData={gifData}>
      <div
        onClick={() => {
          if (view === "dialog") {
            onSelect?.(gifData);
          }
        }}
        ref={ref}
        className="tw-group tw-relative"
        style={{
          ...style,
          height,
        }}
      >
        <div className="tw-absolute tw-left-0 tw-top-0 tw-size-full tw-animate-skeleton-loading tw-rounded tw-bg-blue-50" />
        {isMultiSelect && <MediaLibFileCheckbox isSelected={isSelected} />}

        {isIntersecting && (
          <img
            key={gifData.id}
            loading="lazy"
            alt={gifData.alt}
            src={gifData.preview?.webp || gifData.url}
            className={cn(
              "tw-relative tw-z-10 tw-size-full tw-cursor-pointer tw-rounded tw-object-cover",
              "tw-border tw-border-solid tw-border-primary-900/20",
              "tw-outline-none tw-outline-1 tw-outline-offset-1 tw-transition-[outline-color] hover:tw-outline-primary",
              "tw-opacity-0 tw-transition-opacity tw-duration-300",
              "hover:tw-opacity-95",
            )}
            onLoad={(e) => {
              const img = e.target as HTMLImageElement;
              img.classList.remove("tw-opacity-0");
              img.classList.add("tw-opacity-100");
            }}
          />
        )}

        {gifData.userUrl && (
          <div
            className="tw-absolute tw-bottom-0 tw-left-0 tw-z-10 tw-w-full tw-rounded-b tw-px-1 tw-pb-2 tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100"
            style={{
              background:
                "linear-gradient(0deg, #0D0D0D 9%, rgba(13, 13, 13, 0.00) 100%)",
            }}
          >
            <a
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="tw-flex tw-items-center tw-text-[10px] tw-font-semibold !tw-text-white tw-underline visited:tw-text-white hover:tw-text-white active:tw-text-white"
              rel="noopener noreferrer"
              href={gifData.userUrl}
              target="_blank"
            >
              <Icon
                icon="ri-external-link-line"
                className="tw-mr-1 tw-text-[12px]/none tw-text-white"
              />
              {gifData.userName}
            </a>
          </div>
        )}
      </div>
    </Wrapper>
  );
};

const Wrapper = memo(
  ({ children, gifData }: { children: ReactNode; gifData: IGifs }) => {
    const { elementWrapper } = useGifsCtx();
    if (!elementWrapper) return <>{children}</>;
    const ElementWrapper = elementWrapper;

    return (
      <ElementWrapper element={makeImagesPanelImageElement(gifData)}>
        {children}
      </ElementWrapper>
    );
  },
);
